
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getPageSEO } from '@/config/seoConfig';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  noindex?: boolean;
}

export const SEOHead = ({ 
  title,
  description,
  keywords,
  image = "/image.png",
  url,
  noindex = false
}: SEOHeadProps) => {
  const location = useLocation();
  const baseUrl = 'https://oreog.netlify.app';
  
  // Get page-specific SEO config if not provided
  const pageSEO = getPageSEO(location.pathname);
  const finalTitle = title || pageSEO.title;
  const finalDescription = description || pageSEO.description;
  const finalKeywords = keywords || pageSEO.keywords;
  const finalImage = image || pageSEO.ogImage || '/image.png';
  const finalUrl = url || `${baseUrl}${location.pathname}`;
  const finalNoindex = noindex || pageSEO.noindex || false;
  
  
  useEffect(() => {
    // Update document title
    document.title = finalTitle;
    
    // Update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };
    
    // Basic meta tags
    updateMetaTag('description', finalDescription);
    updateMetaTag('keywords', finalKeywords);
    updateMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow');
    updateMetaTag('googlebot', noindex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
    updateMetaTag('author', 'Oreog');
    
    // Open Graph tags
    updateMetaTag('og:title', finalTitle, true);
    updateMetaTag('og:description', finalDescription, true);
    updateMetaTag('og:image', `${baseUrl}${finalImage}`, true);
    updateMetaTag('og:url', finalUrl, true);
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:site_name', 'Oreog', true);
    updateMetaTag('og:locale', 'en_US', true);
    
    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', finalTitle);
    updateMetaTag('twitter:description', finalDescription);
    updateMetaTag('twitter:image', `${baseUrl}${finalImage}`);
    updateMetaTag('twitter:site', '@oreog_ai');
    updateMetaTag('twitter:creator', '@oreog_ai');
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', finalUrl);
    
  }, [finalTitle, finalDescription, finalKeywords, finalImage, finalUrl, noindex, baseUrl]);
  
  return null;
};
